import React, {Fragment} from 'react';
import {Col, Row, Icon, Input, message, Upload, Form} from 'antd';
import {createRequest, updateRequest, fetchItemRequest} from "@actions/image";
import {connect} from "react-redux";
import {ApplicationState} from "@reducers/index";
import ModalForm from "@hocs/ModalForm";
import {FormattedMessage} from 'react-intl';
import TagSelection, {tagMultiSelectionDefaultReturnValue, TagMultiSelectionReturnValue} from "@components/tags/Selection";
import {SelectionMode} from "@models/index";
import {Tag} from "@models/tag";
import {Image} from "@models/image";
import imageApi from "@api/image";

interface PropsFromState {
  loading: boolean
  errors?: string,
}

interface PropsFromDispatch {
  createRequest: typeof createRequest
  updateRequest: typeof updateRequest
  fetchItemRequest: typeof fetchItemRequest
}

type UploaderAllProps = PropsFromState & PropsFromDispatch & {
  image?: Image;
  className?: string,
  tags: Tag[]
}


type UploaderProps = {
  onProgress: (event: { percent: number }) => void,
  onError: (event: Error, body?: Object) => void,
  onSuccess: (body: Object) => void,
  data: object,
  filename: string,
  file: File,
  withCredentials: boolean,
  action: string,
  headers: object,
}


class ImageForm extends React.Component<UploaderAllProps> {
  
  constructor(props) {
    super(props);
    const {image} = this.props;
    this.state = {
      visible: false,
      previewImage: '',
      fileList: [],
      tags: [],
      uploading: false,
      percent: 0,
      ...image ? this.getStateFromProps(image) : {}
    }
  }
  
  
  private tags: TagMultiSelectionReturnValue = tagMultiSelectionDefaultReturnValue;
  
  componentDidMount() {
    if (this.state.id) {
      this.fetchItem();
    }
  }
  
  fetchItem = () => {
    imageApi.getItem({
        params: {
          id: this.state.id,
        }
      })
      .then(({data}) => {
        const obj = this.getStateFromProps(data);
        this.setState(obj);
      })
      .catch((error) => console.log(error))
  };
  
  getStateFromProps = (image: Image) => {
    return {
      id: image.id,
      title: image.title,
      tags: image.tags || []
    }
  };
  
  
  /**
   * This function is used in image picker
   * @param onSuccessCallback
   */
  upload = (onSuccessCallback) => {
    const values = this.getValues();
    if (values) {
      this.props.createRequest({
        payload: values.payload,
        onSuccessCallback: onSuccessCallback
      })
    }
  };
  
  getValues = () => {
    const title = this.props.form.getFieldValue('title');
    let returnValues = {
      tags: this.tags,
      onProgressCallback: this.onProgress
    };
    
    
    if (this.props.image) {
      returnValues = {
        ...returnValues,
        title
      }
    } else {
      const {fileList} = this.state;
      const file = fileList[0];
      let hasError = false;
      
      this.props.form.setFieldsValue({
        "new_image": file ? file.uid : ""
      });
      this.props.form.validateFieldsAndScroll((err, values) => {
        if (err) {
          hasError = true;
        }
      });
      
      if (!!hasError) {
        return null;
      }
      
      this.setState({
        uploading: true
      });
      
      if (file.size == 0) {
        message.error("Could not upload an empty file!");
        return;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      returnValues = {
        ...returnValues,
        formData,
      }
    }
    
    return {
      payload: returnValues,
      id: this.state.id,
    };
  };
  
  onProgress = (data) => {
    this.setState({
      percent: Math.round(data.loaded / data.total * 100),
      visible: Math.round(data.loaded / data.total * 100) != 100
    })
  };
  
  onRemove = file => {
    this.setState(state => {
      const index = this.state.fileList.indexOf(file);
      const newFileList = this.state.fileList.slice();
      newFileList.splice(index, 1);
      return {
        fileList: newFileList,
      };
    });
  };
  
  beforeUpload = (file) => {
    this.setState(state => ({
      fileList: [file],
    }));
    return false;
  };
  
  handleChange = info => {
    if (info.file.status === 'uploading') {
      this.setState({loading: true});
      return;
    }
    
    const reader = new FileReader();
    reader.addEventListener('load', () => this.setState({
      previewImage: reader.result
    }));
    reader.readAsDataURL(info.file);
  };
  
  onChangeTags = (tags: TagMultiSelectionReturnValue) => {
    this.tags = tags;
  };
  
  getImageBox = () => {
    if (!this.props.image) {
      return (
        <Upload
          className="image-uploader"
          showUploadList={false}
          accept={`image/*`}
          onChange={this.handleChange}
          onRemov={this.onRemove}
          beforeUpload={this.beforeUpload}
          fileList={this.state.fileList}>
          <div className={`image-wrapper`}
               style={{backgroundImage: `url(${this.state.previewImage})`, marginBottom: "10px"}}>
            <Icon type="plus"/>
          </div>
        </Upload>
      )
    } else {
      return (
        <div className={`image-wrapper read-only`}
             style={{backgroundImage: `url(${this.props.image.path})`}}></div>
      )
    }
  };
  
  render() {
    const {getFieldDecorator} = this.props.form;
    return (
      <div className={'form-wrapper'}>
        <Form className={'image-form'}>
          <Row>
            <Col span={8}>
              <Form.Item className={'no-label'}>
                {getFieldDecorator("new_image", {
                  rules: [{
                    // whitespace: true,
                    required: true,
                    message: <FormattedMessage id="app.image_is_required"
                                               defaultMessage={`Image is required!`}/>
                  }]
                })(this.getImageBox())}
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item label={<FormattedMessage id="app.title" defaultMessage={`Title`}/>}>
                {getFieldDecorator("title", {
                  initialValue: this.state.title,
                  rules: [{
                    whitespace: true,
                    required: true,
                    message: <FormattedMessage id="app.title_is_required"
                                               defaultMessage={`Title is required!`}/>
                  }]
                })(<Input style={{marginBottom: "10px"}}/>)}
              </Form.Item>
              
              <Form.Item label={<FormattedMessage id="app.tags" defaultMessage={`Tags`}/>}>
                {getFieldDecorator("tags", {})(
                  <Input type={`hidden`}/>
                )}
                <TagSelection mode={SelectionMode.MULTIPLE}
                              onChange={this.onChangeTags}
                              initialValue={this.state.tags}/>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    );
  }
}


const mapStateToProps = ({images}: ApplicationState) => ({
  loading: images.loading,
  errors: images.errors,
});

const mapDispatchToProps = {
  createRequest,
  updateRequest,
  fetchItemRequest
};


const WrappedImageForm = Form.create({name: "image-form"})(ImageForm);

/**
 * This module us used in image picker
 */
export const ConnectedWrappedImageForm = connect(mapStateToProps, mapDispatchToProps)(WrappedImageForm);

export default connect(mapStateToProps, mapDispatchToProps)(ModalForm(ConnectedWrappedImageForm));
